import { Link } from 'expo-router';
import {
  Car,
  CheckCircle2,
  MessageCircle,
  QrCode,
  Share2,
  Sparkles,
  UserPlus,
  Users,
  Wrench,
} from 'lucide-react-native';
import type { ComponentType } from 'react';
import { StyleSheet, Text, useWindowDimensions, View } from 'react-native';

import { BrandLogo } from '@/src/components/BrandLogo';
import { Button } from '@/src/components/Button';
import { PublicPageShell } from '@/src/components/PublicPageShell';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

type LucideIcon = ComponentType<{
  color?: string;
  size?: number;
  strokeWidth?: number;
}>;

type GaragePoint = {
  description: string;
  Icon: LucideIcon;
  title: string;
};

const garagePoints: GaragePoint[] = [
  {
    description:
      'Create a dedicated Garage profile for the cars you own, love, or are building.',
    Icon: Car,
    title: 'Show your cars',
  },
  {
    description:
      'Add photos, mods, stories, and comments so the build lives in one place.',
    Icon: Wrench,
    title: 'Track the build',
  },
  {
    description:
      'Share Garage vehicles inside Frienzzi or send clean public links to friends.',
    Icon: Share2,
    title: 'Easy sharing',
  },
  {
    description:
      'Print a QR placard for car meets so people can scan your vehicle and see the full build.',
    Icon: QrCode,
    title: 'QR meet cards',
  },
  {
    description:
      'React and comment without turning every post into the usual internet noise.',
    Icon: MessageCircle,
    title: 'Better conversation',
  },
];

const reasons = [
  'Built for real automotive communities, not anonymous arguments.',
  'Public Garage pages make it easy to show a car to anyone.',
  'Frienzzone keeps your favorite builders and friends easier to follow.',
  'Invite links help you bring your car friends into the same community.',
] as const;

function GarageFeatureCard({ description, Icon, title }: GaragePoint) {
  return (
    <View style={styles.featureCard}>
      <View style={styles.featureIcon}>
        <Icon color={colors.primary} size={25} strokeWidth={2.6} />
      </View>
      <Text style={styles.featureTitle}>{title}</Text>
      <Text style={styles.featureDescription}>{description}</Text>
    </View>
  );
}

function GaragePreview({ compact }: { compact: boolean }) {
  return (
    <View style={[styles.preview, compact ? styles.previewCompact : null]}>
      <View style={styles.previewTop}>
        <View style={styles.previewBadge}>
          <Car color={colors.surface} size={28} strokeWidth={2.7} />
        </View>
        <View style={styles.previewTitleGroup}>
          <Text style={styles.previewEyebrow}>Frienzzi Garage</Text>
          <Text style={styles.previewTitle}>Your build has a home.</Text>
        </View>
      </View>
      <View style={[styles.carFrame, compact ? styles.carFrameCompact : null]}>
        <View style={styles.carRoof} />
        <View style={styles.carBody}>
          <View style={styles.carWindow} />
          <View style={styles.carStripe} />
        </View>
        <View style={styles.wheelRow}>
          <View style={styles.wheel} />
          <View style={styles.wheel} />
        </View>
      </View>
      <View style={styles.previewStats}>
        <View style={styles.previewStat}>
          <Text style={styles.previewStatValue}>Photos</Text>
          <Text style={styles.previewStatLabel}>Show the details</Text>
        </View>
        <View style={styles.previewStat}>
          <Text style={styles.previewStatValue}>Mods</Text>
          <Text style={styles.previewStatLabel}>Tell the story</Text>
        </View>
      </View>
    </View>
  );
}

export default function GarageCommunityLandingPage() {
  const { width } = useWindowDimensions();
  const compact = width < 760;
  const sectionTitleStyle = [
    styles.sectionTitle,
    compact ? styles.sectionTitleCompact : null,
  ];

  return (
    <PublicPageShell maxWidth={1120} showFooterAddress={false}>
      <View style={[styles.hero, compact ? styles.heroCompact : null]}>
        <View style={[styles.heroCopy, compact ? styles.flexPanelCompact : null]}>
          <View style={styles.brandRow}>
            <BrandLogo variant="icon" size="md" />
            <Text
              style={[styles.brandText, compact ? styles.brandTextCompact : null]}
              numberOfLines={1}
            >
              frienzzi garage
            </Text>
          </View>
          <Text style={[styles.heroTitle, compact ? styles.heroTitleCompact : null]}>
            A more positive place to share your garage.
          </Text>
          <Text
            style={[
              styles.heroSubtitle,
              compact ? styles.heroSubtitleCompact : null,
            ]}
          >
            Build a Frienzzi profile for your cars, share photos and mods, print
            QR placards for car meets, invite car friends, and keep the
            conversation around the builds people actually care about.
          </Text>
          <View
            style={[styles.heroActions, compact ? styles.stackedActions : null]}
          >
            <Link href="/sign-up" asChild>
              <Button
                title="Create your Garage"
                style={compact ? styles.heroButtonFull : styles.heroButton}
              />
            </Link>
            <Link href="/" asChild>
              <Button
                title="See Frienzzi"
                variant="secondary"
                style={compact ? styles.heroButtonFull : styles.heroButton}
              />
            </Link>
          </View>
        </View>
        <GaragePreview compact={compact} />
      </View>

      <View style={[styles.reasonBand, compact ? styles.stackBand : null]}>
        <View style={[styles.reasonCopy, compact ? styles.flexPanelCompact : null]}>
          <Text style={styles.eyebrow}>For automotive enthusiasts</Text>
          <Text style={sectionTitleStyle}>
            Share the car, the build, the QR code, and the story without losing
            it in a noisy feed.
          </Text>
        </View>
        <View style={[styles.reasonList, compact ? styles.flexPanelCompact : null]}>
          {reasons.map((reason) => (
            <View key={reason} style={styles.reasonRow}>
              <CheckCircle2 color={colors.success} size={22} strokeWidth={2.6} />
              <Text style={styles.reasonText}>{reason}</Text>
            </View>
          ))}
        </View>
      </View>

      <View style={styles.featureSection}>
        <View style={styles.sectionHeader}>
          <Text style={styles.eyebrow}>Garage features</Text>
          <Text style={sectionTitleStyle}>
            Built around the way car people share.
          </Text>
        </View>
        <View style={styles.featureGrid}>
          {garagePoints.map((point) => (
            <GarageFeatureCard key={point.title} {...point} />
          ))}
        </View>
      </View>

      <View style={[styles.communityBand, compact ? styles.calloutBandCompact : null]}>
        <View style={styles.communityIcon}>
          <QrCode color={colors.surface} size={30} strokeWidth={2.6} />
        </View>
        <View
          style={[styles.communityCopy, compact ? styles.flexPanelCompact : null]}
        >
          <Text style={styles.eyebrow}>Built for car meets</Text>
          <Text
            style={[
              styles.communityTitle,
              compact ? styles.calloutTitleCompact : null,
            ]}
          >
            Put a Frienzzi QR placard on the car and let people scan the full
            build.
          </Text>
          <Text style={styles.communityBody}>
            Garage vehicles can generate printable QR placards. Bring them to
            meets, shows, or cruises so people can open your vehicle page,
            photos, mods, and story without hunting for your profile.
          </Text>
        </View>
        {compact ? null : (
          <Users color={colors.primary} size={42} strokeWidth={2.4} />
        )}
      </View>

      <View style={[styles.inviteBand, compact ? styles.calloutBandCompact : null]}>
        <View style={styles.inviteIcon}>
          <UserPlus color={colors.surface} size={28} strokeWidth={2.6} />
        </View>
        <View style={[styles.inviteCopy, compact ? styles.flexPanelCompact : null]}>
          <Text style={styles.eyebrow}>Bring your people</Text>
          <Text
            style={[
              styles.inviteTitle,
              compact ? styles.calloutTitleCompact : null,
            ]}
          >
            Invite car friends and start building a better feed together.
          </Text>
          <Text style={styles.inviteBody}>
            Every Frienzzi user gets an invite link. Share it with your club,
            build group, or friends, then keep up with their posts and Garage
            updates in one place.
          </Text>
        </View>
      </View>

      <View style={[styles.finalCta, compact ? styles.finalCtaCompact : null]}>
        <Sparkles color={colors.primary} size={34} strokeWidth={2.5} />
        <Text style={[styles.finalTitle, compact ? styles.finalTitleCompact : null]}>
          Start your Frienzzi Garage.
        </Text>
        <Text style={styles.finalBody}>
          Create your profile, add your first vehicle, print a QR placard, and
          share it with the people who already ask what you are building next.
        </Text>
        <Link href="/sign-up" asChild>
          <Button
            title="Join Frienzzi"
            style={compact ? styles.finalButtonFull : styles.finalButton}
          />
        </Link>
      </View>
    </PublicPageShell>
  );
}

const styles = StyleSheet.create({
  brandRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
  },
  brandText: {
    color: colors.text,
    fontSize: 28,
    fontWeight: '900',
    lineHeight: 34,
  },
  brandTextCompact: {
    flex: 1,
    fontSize: 23,
    lineHeight: 28,
    minWidth: 0,
  },
  carBody: {
    backgroundColor: colors.surface,
    borderColor: colors.text,
    borderRadius: 8,
    borderWidth: 3,
    height: 112,
    justifyContent: 'center',
    paddingHorizontal: spacing.xl,
  },
  carFrame: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.xl,
  },
  carFrameCompact: {
    padding: spacing.lg,
  },
  carRoof: {
    alignSelf: 'center',
    backgroundColor: colors.accentSoft,
    borderColor: colors.text,
    borderRadius: 8,
    borderWidth: 3,
    height: 58,
    width: '58%',
  },
  carStripe: {
    backgroundColor: colors.primary,
    borderRadius: 999,
    height: 10,
    marginTop: spacing.md,
    width: '54%',
  },
  carWindow: {
    alignSelf: 'flex-end',
    backgroundColor: colors.accentSoft,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    height: 36,
    width: '36%',
  },
  communityBand: {
    alignItems: 'center',
    backgroundColor: colors.text,
    borderRadius: 8,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xl,
    padding: spacing.xl,
  },
  communityBody: {
    color: 'rgba(255, 255, 255, 0.78)',
    fontSize: 16,
    lineHeight: 24,
  },
  communityCopy: {
    flexBasis: 460,
    flexGrow: 1,
    flexShrink: 1,
    gap: spacing.sm,
    minWidth: 0,
  },
  communityIcon: {
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderRadius: 8,
    height: 58,
    justifyContent: 'center',
    width: 58,
  },
  communityTitle: {
    color: colors.surface,
    fontSize: 25,
    fontWeight: '900',
    lineHeight: 32,
  },
  eyebrow: {
    color: colors.secondary,
    fontSize: 14,
    fontWeight: '900',
    letterSpacing: 0,
    lineHeight: 18,
    textTransform: 'uppercase',
  },
  featureCard: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexBasis: 240,
    flexGrow: 1,
    flexShrink: 1,
    gap: spacing.md,
    maxWidth: '100%',
    minWidth: 0,
    padding: spacing.xl,
  },
  featureDescription: {
    color: colors.mutedText,
    fontSize: 15,
    lineHeight: 22,
  },
  featureGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.lg,
  },
  featureIcon: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    height: 50,
    justifyContent: 'center',
    width: 50,
  },
  featureSection: {
    gap: spacing.xl,
  },
  featureTitle: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
    lineHeight: 26,
  },
  finalBody: {
    color: colors.mutedText,
    fontSize: 18,
    lineHeight: 27,
    maxWidth: 700,
    textAlign: 'center',
  },
  finalButton: {
    minWidth: 190,
  },
  finalButtonFull: {
    minWidth: 190,
    width: '100%',
  },
  finalCta: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.lg,
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.xxl,
  },
  finalCtaCompact: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xl,
  },
  finalTitle: {
    color: colors.text,
    fontSize: 32,
    fontWeight: '900',
    lineHeight: 39,
    textAlign: 'center',
  },
  finalTitleCompact: {
    fontSize: 27,
    lineHeight: 33,
  },
  flexPanelCompact: {
    flexBasis: 'auto',
    flexGrow: 0,
    flexShrink: 1,
    maxWidth: '100%',
    width: '100%',
  },
  hero: {
    alignItems: 'stretch',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xxl,
    padding: spacing.xxl,
  },
  heroCompact: {
    flexDirection: 'column',
    gap: spacing.lg,
    padding: spacing.lg,
  },
  heroActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  heroButton: {
    minWidth: 180,
  },
  heroButtonFull: {
    minWidth: 180,
    width: '100%',
  },
  heroCopy: {
    flexBasis: 440,
    flexGrow: 1,
    flexShrink: 1,
    gap: spacing.lg,
    justifyContent: 'center',
    minWidth: 0,
  },
  heroSubtitle: {
    color: colors.mutedText,
    fontSize: 20,
    fontWeight: '700',
    lineHeight: 30,
  },
  heroSubtitleCompact: {
    fontSize: 17,
    lineHeight: 25,
    maxWidth: '100%',
    width: '100%',
  },
  heroTitle: {
    color: colors.text,
    fontSize: 58,
    fontWeight: '900',
    lineHeight: 64,
  },
  heroTitleCompact: {
    fontSize: 34,
    lineHeight: 40,
    maxWidth: '100%',
    width: '100%',
  },
  inviteBand: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xl,
    padding: spacing.xl,
  },
  inviteBody: {
    color: colors.mutedText,
    fontSize: 16,
    lineHeight: 24,
  },
  inviteCopy: {
    flexBasis: 460,
    flexGrow: 1,
    flexShrink: 1,
    gap: spacing.sm,
    minWidth: 0,
  },
  inviteIcon: {
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderRadius: 8,
    height: 58,
    justifyContent: 'center',
    width: 58,
  },
  inviteTitle: {
    color: colors.text,
    fontSize: 25,
    fontWeight: '900',
    lineHeight: 32,
  },
  preview: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexBasis: 360,
    flexGrow: 1,
    flexShrink: 1,
    gap: spacing.lg,
    maxWidth: '100%',
    minWidth: 0,
    padding: spacing.xl,
  },
  previewCompact: {
    alignSelf: 'stretch',
    flexBasis: 'auto',
    padding: spacing.lg,
    width: 'auto',
  },
  previewBadge: {
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderRadius: 8,
    height: 58,
    justifyContent: 'center',
    width: 58,
  },
  previewEyebrow: {
    color: colors.secondary,
    fontSize: 13,
    fontWeight: '900',
    letterSpacing: 0,
    lineHeight: 17,
    textTransform: 'uppercase',
  },
  previewStat: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flex: 1,
    gap: spacing.xs,
    minWidth: 130,
    padding: spacing.md,
  },
  previewStatLabel: {
    color: colors.mutedText,
    fontSize: 13,
    fontWeight: '800',
    lineHeight: 18,
  },
  previewStats: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  previewStatValue: {
    color: colors.text,
    fontSize: 19,
    fontWeight: '900',
    lineHeight: 24,
  },
  previewTitle: {
    color: colors.text,
    fontSize: 24,
    fontWeight: '900',
    lineHeight: 30,
  },
  previewTitleGroup: {
    flex: 1,
    gap: spacing.xs,
    minWidth: 0,
  },
  previewTop: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
  },
  reasonBand: {
    alignItems: 'stretch',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xl,
  },
  reasonCopy: {
    flexBasis: 360,
    flexGrow: 1,
    flexShrink: 1,
    gap: spacing.md,
    justifyContent: 'center',
    minWidth: 0,
  },
  reasonList: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexBasis: 420,
    flexGrow: 1,
    flexShrink: 1,
    gap: spacing.md,
    minWidth: 0,
    padding: spacing.xl,
  },
  reasonRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.md,
  },
  reasonText: {
    color: colors.text,
    flex: 1,
    fontSize: 16,
    fontWeight: '800',
    lineHeight: 23,
  },
  sectionHeader: {
    gap: spacing.sm,
  },
  sectionTitle: {
    color: colors.text,
    fontSize: 34,
    fontWeight: '900',
    lineHeight: 42,
  },
  sectionTitleCompact: {
    fontSize: 27,
    lineHeight: 34,
    maxWidth: '100%',
  },
  stackBand: {
    flexDirection: 'column',
  },
  stackedActions: {
    alignItems: 'stretch',
    flexDirection: 'column',
    width: '100%',
  },
  calloutBandCompact: {
    alignItems: 'stretch',
    flexDirection: 'column',
    gap: spacing.lg,
    padding: spacing.lg,
  },
  calloutTitleCompact: {
    fontSize: 21,
    lineHeight: 27,
  },
  wheel: {
    backgroundColor: colors.text,
    borderColor: colors.surface,
    borderRadius: 999,
    borderWidth: 5,
    height: 54,
    width: 54,
  },
  wheelRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: -spacing.lg,
  },
});
